import React, { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import AIPanel from "../components/AIPanel";
import { Youtube, ExternalLink, Star, ThumbsUp } from "lucide-react";
import { toast } from "sonner";

export function youtubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

export default function ReadingPlans() {
  const [videos, setVideos] = useState([]);
  const [aiBook, setAiBook] = useState("");

  useEffect(() => {
    api.get("/study-videos").then((r) => setVideos(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  return (
    <div className="space-y-10">
      <div className="fade-up">
        <p className="text-xs tracking-[0.2em] uppercase text-[#C46B4E] font-semibold">The Word</p>
        <h1 className="font-heading text-5xl text-[#2A2621] mt-2">Word &amp; Wellness</h1>
        <p className="text-[#615A50] mt-2">Rest, reflect, and grow — Scripture meets everyday wellness.</p>
      </div>

      <div className="space-y-8">
        {videos.map((v) => {
          const id = youtubeId(v.youtube_url);
          return (
            <div key={v.id} className="rounded-2xl border border-[#D5CEBF] bg-[#F3EFE4] overflow-hidden fade-up" data-testid={`study-video-${v.id}`}>
              {id ? (
                <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${id}`}
                    title={v.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    data-testid={`study-video-frame-${v.id}`}
                  />
                </div>
              ) : (
                <a href={v.youtube_url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-6 text-[#3D5844] hover:bg-[#E7DFCE] transition-colors" data-testid={`study-video-link-${v.id}`}>
                  <Youtube className="w-6 h-6" /> Watch on YouTube <ExternalLink className="w-4 h-4" />
                </a>
              )}
              <div className="p-6">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-[#C46B4E] text-sm"><Youtube className="w-4 h-4" /> Word &amp; Wellness</span>
                  {v.featured && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#C46B4E] text-white font-medium" data-testid={`study-video-featured-${v.id}`}><Star className="w-3 h-3" /> Featured</span>}
                </div>
                <h3 className="font-heading text-3xl text-[#2A2621] mt-1">{v.title}</h3>
                {v.description && <p className="text-[#615A50] mt-2 leading-relaxed">{v.description}</p>}
                {v.notes && (
                  <div className="mt-5 rounded-xl border border-[#D5CEBF] bg-[#FAF7EE] p-5" data-testid={`study-video-notes-${v.id}`}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#3D5844] mb-2">Notes &amp; discussion</p>
                    <p className="text-[#2A2621] leading-relaxed whitespace-pre-wrap text-[15px]">{v.notes}</p>
                  </div>
                )}
                {id && (
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <a href={`https://www.youtube.com/watch?v=${id}`} target="_blank" rel="noopener noreferrer"
                      data-testid={`study-video-like-${v.id}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C46B4E] text-white text-sm font-medium hover:bg-[#AC5B40] transition-colors">
                      <ThumbsUp className="w-4 h-4" /> Like on YouTube <ExternalLink className="w-3.5 h-3.5 opacity-80" />
                    </a>
                    <a href="https://www.youtube.com/@DJWashington-b9H?sub_confirmation=1" target="_blank" rel="noopener noreferrer"
                      data-testid={`study-video-subscribe-${v.id}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D5CEBF] text-[#3D5844] text-sm font-medium hover:bg-[#E7DFCE] transition-colors">
                      <Youtube className="w-4 h-4" /> Subscribe on YouTube <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {videos.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#D5CEBF] bg-[#F3EFE4] p-10 text-center" data-testid="study-videos-empty">
            <Youtube className="w-8 h-8 text-[#3D5844]/50 mx-auto" />
            <p className="text-[#615A50] mt-3">No studies posted yet — a leader will share one soon.</p>
          </div>
        )}
      </div>

      <div>
        <h2 className="font-heading text-3xl text-[#2A2621] mb-2">Scripture reflection companion</h2>
        <p className="text-[#615A50] mb-5">Enter a passage or theme to receive gentle, Spirit-led prompts (KJV) for your study.</p>
        <input value={aiBook} onChange={(e) => setAiBook(e.target.value)} placeholder="e.g. Psalm 23, or the theme 'peace'" data-testid="ai-book-input"
          className="w-full max-w-md rounded-xl border border-[#D5CEBF] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#3D5844]/40 mb-5" />
        <div className="grid md:grid-cols-2 gap-6">
          <AIPanel title="Reflection Questions" description="Open-ended questions to explore Scripture together." endpoint="/ai/discussion-prompts" payload={{ book_title: aiBook || "your current passage" }} testid="ai-prompts" />
          <AIPanel title="Reflection & Prayer Prompts" description="Quiet prompts to apply the Word and pray." endpoint="/ai/reflection-questions" payload={{ book_title: aiBook || "your current passage" }} testid="ai-reflection" />
        </div>
      </div>
    </div>
  );
}
